import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Alert from '@material-ui/lab/Alert'
import get from 'lodash/get'
import set from 'lodash/set'
import { DateTime } from 'luxon'
import { useCallback, useState } from 'react'
import {
  ArrayField,
  ArrayInput,
  BooleanInput,
  Create,
  Datagrid,
  FormDataConsumer,
  FormTab,
  FunctionField,
  Link,
  maxLength,
  minLength,
  number,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  useCreate,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin'
import { useFormState } from 'react-final-form'
import { SOURCE_NAME_CHOICES } from 'shared/helpers/constants'

import { LoadStopReferenceCodeInput } from 'components/Common/Loads'
import { formatTripRate } from 'helpers/jobs'
import {
  captureExceptionIfProduction,
  getLoadStop,
  getUserFullName,
  localizeDateTimeAndStripOffset,
  sanitizeNonNullableFieldsOnLoad,
  stripOffsetsFromStopDateTimes,
} from 'helpers/utils'
import { getDuplicateJobs } from '../../api/jobs'
import {
  DESTINATION,
  LoadingType,
  LoadingTypeChoices,
  ORIGIN,
  STOP_TYPE_CHOICES,
  StopTypeDetail,
  StopTypeDetailChoices,
  SUPPORTED_EQUIPMENT_CHOICES,
} from '../../helpers/constants'
import { getCountryFromCode } from '../../helpers/countries'
import { getStateChoices } from '../../helpers/states'
import { validateInteger, validateLoadStops, validateTwoDecimals } from '../../helpers/validators'
import { BrokerStatus, REASONS_REQUIRED_BROKER_STATUSES } from '../Brokers/BrokerStatusTransitions'
import { BrokerReferenceInput, BrokerReferenceInputClassName } from '../Common/Brokers'
import {
  LocalizedStopDateTimeField,
  OriginDestinationField,
  StopDateTimeInputWindow,
  StopSequenceNumberWrapper,
} from '../Fields'

// If cloning a load, we want to clear the source id, notes, & existing pks.
// We also want to remove the stop's timezone, as it will be recalculated in the backend
const getLoadInitialValues = (load: any) => {
  if (load) {
    for (var stop of load.stops) {
      stop.date_start = localizeDateTimeAndStripOffset(stop.date_start, stop.location_timezone)
      stop.date_end = localizeDateTimeAndStripOffset(stop.date_end, stop.location_timezone)
      stop.notes = ''
      stop.id = null
      delete stop.location_timezone
    }
    const { id, ...initialLoadValues } = load
    return initialLoadValues
  }

  const defaultDate = DateTime.local().set({ hour: 0, minute: 0 })

  return {
    origin_city: '',
    origin_state: '',
    dest_city: '',
    dest_state: '',
    internal_notes: '',
    origin_pickup_date: '',
    dest_delivery_date: '',
    source_human_id: '',
    trip_stops: 2,
    stops: [
      {
        sequence_number: 1,
        type: ORIGIN,
        type_detail: StopTypeDetail.Pickup,
        loading_type: LoadingType.Live,
        date_start: defaultDate,
        date_end: defaultDate,
      },
      {
        sequence_number: 2,
        type: DESTINATION,
        type_detail: StopTypeDetail.Delivery,
        loading_type: LoadingType.Live,
        date_start: defaultDate,
        date_end: defaultDate,
      },
    ],
  }
}

const validateLoadCreation = (values: any) => {
  var errors = validateLoadStops(values)
  if (errors && errors.load_stops) {
    // Validation in the React Admin create form is expecting an array, not string.
    errors.load_stops = [errors.load_stops]
  }
  return errors
}

const validateTripRate = [number(), validateTwoDecimals]

const SaveButtonAltered = (props: any) => {
  const notify = useNotify()
  const redirectTo = useRedirect()
  const [create] = useCreate('loads')
  const { basePath } = props
  const formState = useFormState()
  const [openBrokerStatusConfirmDialog, setOpenBrokerStatusConfirmDialog] = useState(false)
  const [openDuplicateJobsDialog, setOpenDuplicateJobsDialog] = useState(false)
  const [brokerStatus, setBrokerStatus] = useState(undefined)
  const [duplicateJobs, setDuplicateJobs] = useState(undefined)
  const [redirect, setRedirect] = useState(undefined)
  const [formValues, setFormValues] = useState(undefined)
  const dataProvider = useDataProvider()

  const submitForm = useCallback(
    (values, redirect) => {
      if (formState.hasValidationErrors) {
        return
      }
      values = stripOffsetsFromStopDateTimes(values, false)
      values = sanitizeNonNullableFieldsOnLoad(values, false)
      let originStop = getLoadStop(values, ORIGIN)
      let destStop = getLoadStop(values, DESTINATION)
      let numStops = values.stops.length
      let equipment = typeof values.equipment === 'string' ? [values.equipment] : values.equipment
      let sortedStops = values.stops.sort((a: any, b: any) => a.date_start > b.date_start)
      set(values, 'origin_city', originStop.location_city)
      set(values, 'origin_state', originStop.location_state)
      set(values, 'dest_city', destStop.location_city)
      set(values, 'dest_state', destStop.location_state)
      set(values, 'origin_pickup_date', originStop.date_start)
      set(values, 'dest_delivery_date', destStop.date_start)
      set(values, 'source_human_id', values.source_id)
      set(values, 'equipment', equipment)
      set(values, 'trip_stops', numStops)
      set(values, 'stops', sortedStops)
      create(
        {
          payload: { data: { ...values } },
        },
        {
          onSuccess: ({ data: newRecord }: any) => {
            notify('Load saved', 'info', { smart_count: 1 })
            redirectTo(redirect, basePath, newRecord.id, newRecord)
          },
          onFailure: (error: any) => {
            notify(`${error.message}, ${error.response?.data}`, { type: 'warning' })
          },
        }
      )
    },
    [create, notify, basePath, redirectTo, formState]
  )

  const checkDuplicateJobs = (values: any, redirect: any) => {
    let originStop = getLoadStop(values, ORIGIN)
    let destStop = getLoadStop(values, DESTINATION)
    getDuplicateJobs({
      source_human_id: values.source_id,
      origin_pickup_date: originStop.date_start?.toString(),
      origin_city: originStop.location_city,
      origin_state: originStop.location_state,
      dest_delivery_date: destStop.date_start?.toString(),
      dest_city: destStop.location_city,
      dest_state: destStop.location_state,
    }).then((jobs) => {
      if (jobs?.length > 0) {
        setDuplicateJobs(jobs)
        setFormValues(values)
        setRedirect(redirect)
        setOpenDuplicateJobsDialog(true)
      } else {
        checkBrokerStatus(values, redirect)
      }
    })
  }

  const checkBrokerStatus = (values: any, redirect: any) => {
    /* Warn the user if the broker is SUSPENDED/DISQUALIFIED */
    dataProvider
      .getOne('brokers', { id: values.broker_id })
      .then((response) => {
        const broker = response.data

        if (REASONS_REQUIRED_BROKER_STATUSES.includes(broker.status)) {
          setBrokerStatus(broker.status)
          setFormValues(values)
          setRedirect(redirect)
          setOpenBrokerStatusConfirmDialog(true)
        } else {
          submitForm(values, redirect)
        }
      })
      .catch((error) => {
        notify(`${error.message}`, { type: 'warning' })
        captureExceptionIfProduction(error)
      })
  }

  const loadStopErrors = formState.errors?.load_stops
  if (loadStopErrors) {
    let errorString = `There is an error with the form: ${loadStopErrors}`
    return <Alert severity="error">{errorString}</Alert>
  }
  return (
    <div>
      <SaveButton {...props} onSave={checkDuplicateJobs} />
      <BrokerStatusConfirmDialog
        status={brokerStatus}
        openBrokerStatusConfirmDialog={openBrokerStatusConfirmDialog}
        setOpenBrokerStatusConfirmDialog={setOpenBrokerStatusConfirmDialog}
        onSave={() => {
          submitForm(formValues, redirect)
          setOpenBrokerStatusConfirmDialog(false)
        }}
        redirectTo={redirectTo}
        redirect={redirect}
        basePath={basePath}
        formValues={formValues}
      />
      <DuplicateJobWarningDialog
        duplicateJobs={duplicateJobs}
        openDuplicateJobsDialog={openDuplicateJobsDialog}
        setOpenDuplicateJobsDialog={setOpenDuplicateJobsDialog}
        onSave={() => {
          setOpenDuplicateJobsDialog(false)
          checkBrokerStatus(formValues, redirect)
        }}
        redirectTo={redirectTo}
        redirect={redirect}
        basePath={basePath}
        formValues={formValues}
      />
    </div>
  )
}

const LoadCreateToolbar = (props: any) => (
  <Toolbar {...props}>
    <SaveButtonAltered label="Save" redirect="show" submitOnEnter={false} variant="text" />
  </Toolbar>
)

const LoadCreate = (props: any) => {
  const load = get(props, 'location.state.load')
  const broker = get(load, 'broker')
  if (broker) {
    load.broker_id = load.broker.id
  }

  const country = getCountryFromCode('US')
  const stateChoices = getStateChoices(country?.states(true))

  return (
    <Create {...props}>
      <TabbedForm
        toolbar={<LoadCreateToolbar />}
        initialValues={getLoadInitialValues(load)}
        validate={validateLoadCreation}
      >
        <FormTab label="Contact information">
          <SelectInput source="source" choices={SOURCE_NAME_CHOICES} validate={required()} />
          <TextInput source="source_id" label="Source ID" validate={required()} />
          <BrokerReferenceInput
            source="broker_id"
            label="Broker"
            reference="brokers"
            filter={{ status__not: BrokerStatus.Invalid.id }}
            filterToQuery={(searchText: any) => ({ search: searchText })}
            containerClassName={BrokerReferenceInputClassName.load}
            validate={required()}
          />
          <TextInput source="company_name" label="Shipper name" />
          <TextInput source="contact_name" />
          <TextInput source="contact_email" />
          <TextInput source="contact_phone" />
          <TextInput source="internal_notes" multiline fullWidth label="Internal Notes" />
        </FormTab>
        <FormTab label="Stops">
          <ArrayInput source="stops">
            <SimpleFormIterator>
              <StopSequenceNumberWrapper label="Sequence number" validate={required()} />
              <SelectInput
                source="type"
                choices={STOP_TYPE_CHOICES}
                label="Stop type"
                validate={required()}
              />
              <SelectInput
                source="type_detail"
                label="Type detail"
                choices={StopTypeDetailChoices}
                validate={required()}
              />
              <SelectInput source="loading_type" label="Loading type" choices={LoadingTypeChoices} />
              <StopDateTimeInputWindow />
              <TextInput source="location_name" label="Facility name" />
              <TextInput source="location_address1" label="Location address 1" />
              <TextInput source="location_address2" label="Location address 2" />
              <TextInput source="location_city" label="City" validate={required()} />
              <SelectInput
                source="location_state"
                label="State"
                choices={stateChoices}
                validate={[required(), maxLength(2), minLength(2)]}
              />
              <TextInput source="location_zip" label="Zip" />
              <TextInput source="notes" multiline fullWidth label="Notes" />
              {/* Require only the first load stop's reference_code */}
              <FormDataConsumer>
                {({ getSource }) =>
                  getSource ? (
                    <LoadStopReferenceCodeInput
                      referenceCodeSource={getSource('reference_code')}
                      isRequired={getSource('reference_code') === 'stops[0].reference_code'}
                    />
                  ) : null
                }
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
        <FormTab label="Trip details">
          <SelectInput source="equipment" choices={SUPPORTED_EQUIPMENT_CHOICES} validate={required()} />
          <BooleanInput source="is_load_out_trailer" label="Load out trailer" />
          <BooleanInput source="bid_book" />
          <BooleanInput source="instant_book" />
          <BooleanInput source="standard_book" />
          <NumberInput source="trip_rate" validate={validateTripRate} />
          <BooleanInput source="truck_full_load" label="Full truck load" />
          <NumberInput source="truck_length_ft" validate={validateInteger} />
          <NumberInput source="truck_weight_lb" validate={validateInteger} />
        </FormTab>
      </TabbedForm>
    </Create>
  )
}

export const BrokerStatusConfirmDialog = ({
  status,
  openBrokerStatusConfirmDialog,
  setOpenBrokerStatusConfirmDialog,
  onSave,
  redirectTo,
  basePath,
}: any) => {
  return (
    <Dialog open={openBrokerStatusConfirmDialog} onClose={() => setOpenBrokerStatusConfirmDialog(false)}>
      <DialogTitle>The broker is {status}.</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This load is not available to haul for Virtual Carrier drivers.
          <br />
          <br />
          Flex and Cash customers can take this load, but the load is not eligible for instant pay.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button type="submit" variant="contained" color="primary" onClick={onSave}>
          CONTINUE TO SAVE
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          onClick={() => {
            setOpenBrokerStatusConfirmDialog(false)
            redirectTo(basePath)
          }}
        >
          Cancel Load Creation
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const DuplicateJobDescriptions = ({ jobs }: any) => {
  return (
    <ArrayField record={jobs} source="jobs" label="Jobs">
      <Datagrid>
        <FunctionField
          label="Job ID"
          render={(record: any) => <Link to={`/jobs/${record.id}/show`}>{record.human_id}</Link>}
        />
        <TextField label="Broker" source="load.broker.name" />
        <TextField label="Broker Load ID" source="load.source_human_id" />
        <FunctionField
          label="Trip Rate"
          source="load.trip_rate"
          render={(record: any) => formatTripRate(record.load.trip_rate)}
        />
        <FunctionField
          label="Origin &rarr; Destination"
          render={(record: any) => <OriginDestinationField record={record.load} />}
        />
        <LocalizedStopDateTimeField source="load.origin_pickup_date" label="Pickup Time" />
        <LocalizedStopDateTimeField source="load.dest_delivery_date" label="Dropoff Time" />
        <TextField label="Fleet Name" source="team.name" />
        <FunctionField
          label="Assigned Driver"
          render={(record: any) => <span>{getUserFullName(record.assigned_to)}</span>}
        />
      </Datagrid>
    </ArrayField>
  )
}

export const DuplicateJobWarningDialog = ({
  duplicateJobs,
  openDuplicateJobsDialog,
  setOpenDuplicateJobsDialog,
  onSave,
  redirectTo,
  basePath,
  formValues,
}: any) => {
  // if any of the "duplicate" jobs matches the current form values on: broker, AND broker load id, AND trip rate, then do not allow save
  // (if not all match, then user will be warned that something similar exists, but will still be allowed to save)
  const doNotAllowSave =
    duplicateJobs &&
    duplicateJobs.some(
      (job: any) =>
        get(job, 'load.source_human_id') === get(formValues, 'source_human_id') &&
        get(job, 'load.broker.id') === get(formValues, 'broker.id') &&
        get(job, 'load.trip_rate') === get(formValues, 'trip_rate')
    )

  const dialogTitle =
    !duplicateJobs || duplicateJobs?.length === 1
      ? 'Found a job with a similar load'
      : `Found ${duplicateJobs?.length} jobs with a similar load`

  return (
    <Dialog maxWidth="xl" open={openDuplicateJobsDialog} onClose={() => setOpenDuplicateJobsDialog(false)}>
      <DialogTitle>{doNotAllowSave ? 'A job with this load already exists' : dialogTitle}</DialogTitle>
      <DialogContent>
        <DuplicateJobDescriptions jobs={{ jobs: duplicateJobs }} />
      </DialogContent>
      <DialogActions>
        {!doNotAllowSave && (
          <Button type="submit" variant="contained" color="primary" onClick={onSave}>
            Save load anyway
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          onClick={() => {
            setOpenDuplicateJobsDialog(false)
            redirectTo(basePath)
          }}
        >
          {doNotAllowSave ? 'OK' : "Don't save load"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LoadCreate
